/**
 * Secure cookie utilities that minimize client-side cookie exposure
 * Only reads non-sensitive cookies that are safe to expose to JavaScript
 */

/**
 * Safely checks if a user might be authenticated by reading only the public logged_in indicator
 * This is a non-sensitive cookie that only indicates authentication status
 * @returns boolean indicating if user might be authenticated
 */
export const mightBeAuthenticated = (): boolean => {
  try {
    // Early return if no cookies exist
    if (!document.cookie) return false;
    
    // Only read the non-sensitive logged_in indicator cookie
    // This cookie doesn't contain sensitive data, just a boolean indicator
    const cookies = document.cookie.split(';');
    const loggedInCookie = cookies.find(cookie => 
      cookie.trim().startsWith('logged_in=')
    );
    
    if (!loggedInCookie) return false;
    
    const value = loggedInCookie.split('=')[1];
    return value === '1';
  } catch (error) {
    // If we can't read cookies, assume not authenticated for security
    return false;
  }
};

/**
 * Gets CSRF token from XSRF-TOKEN cookie
 * This is necessary for CSRF protection but should be handled carefully
 * @returns CSRF token or null
 */
export const getCsrfToken = (): string | null => {
  try {
    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find(cookie => 
      cookie.trim().startsWith('XSRF-TOKEN=')
    );
    
    if (!csrfCookie) return null;
    
    return csrfCookie.split('=')[1] || null;
  } catch (error) {
    return null;
  }
};

/**
 * Checks if we're in a secure environment (HTTPS)
 * @returns boolean indicating if we're in a secure context
 */
export const isSecureContext = (): boolean => {
  try {
    return window.location.protocol === 'https:' || 
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  } catch (error) {
    return false;
  }
};
